import React, { useCallback, useMemo, useRef, useState } from "react"

import {
  useHumanReviewProjectMutationsTransport,
  useHumanReviewProjectsTransport,
  useHumanReviewProjectWithSimulationIdsFetcherTransport,
  useProjectAssignmentEmailTransport,
} from "@/human-feedback/transport"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSearch,
  DropdownMenuTrigger,
  toast,
  Tooltip,
  TruncatedText,
} from "@coval/ui"
import { Users } from "lucide-react"

import { logger } from "@/lib/logger"

const log = logger("AddToHumanReviewButton")

interface AddToHumanReviewButtonProps {
  simulationOutputIds: string[]
  disabled?: boolean
  title?: string
}

export const AddToHumanReviewButton: React.FC<AddToHumanReviewButtonProps> = ({
  simulationOutputIds,
  disabled = false,
  title,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [query, setQuery] = useState("")
  // The ref blocks a second add synchronously, before the state re-render
  // disables the items.
  const isAddingRef = useRef(false)
  const [isAdding, setIsAdding] = useState(false)

  // Human review project queries and mutations
  const { data: humanReviewProjects } = useHumanReviewProjectsTransport()
  const fetchProjectWithSimulationIds =
    useHumanReviewProjectWithSimulationIdsFetcherTransport()
  const { addSimulationOutputs } = useHumanReviewProjectMutationsTransport()

  // Email notification mutation
  const { mutateAsync: sendProjectUpdate } =
    useProjectAssignmentEmailTransport()

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
    setQuery("")
  }, [])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsMenuOpen(true)
    } else {
      closeMenu()
    }
  }

  const visibleProjects = useMemo(() => {
    const projects = humanReviewProjects ?? []
    // `toLowerCase` folds case the same way in every locale, so a Turkish
    // runtime still matches "istanbul" to "Istanbul".
    const normalizedQuery = query.trim().toLowerCase()
    if (normalizedQuery === "") {
      return projects
    }
    return projects.filter((project) =>
      project.name.toLowerCase().includes(normalizedQuery),
    )
  }, [humanReviewProjects, query])

  const handleAddToHumanReviewProject = useCallback(
    async (projectId: string) => {
      if (!simulationOutputIds.length) {
        toast.danger({
          title: "Error",
          description: "No simulation outputs available to add to project",
        })
        return
      }

      if (isAddingRef.current) {
        return
      }
      isAddingRef.current = true
      setIsAdding(true)

      try {
        const currentProject = await fetchProjectWithSimulationIds(projectId)

        // Check which simulation outputs are already in the project
        const existingSimulationIds = new Set(
          currentProject.linked_simulation_ids || [],
        )
        const newSimulationIds = [...new Set(simulationOutputIds)].filter(
          (id) => !existingSimulationIds.has(id),
        )

        if (newSimulationIds.length === 0) {
          toast.success({
            title: "Already Added",
            description: `All simulation outputs are already in "${currentProject.name}".`,
          })
          closeMenu()
          return
        }

        // An additive delta, so a membership change another reviewer makes
        // between the read above and this write is kept.
        await addSimulationOutputs({
          id: projectId,
          simulationOutputs: newSimulationIds,
        })

        toast.success({
          title: "Success",
          description: `Added ${newSimulationIds.length} simulation${newSimulationIds.length === 1 ? "" : "s"} to "${currentProject.name}".`,
        })
        closeMenu()

        // Send email notification to all assignees automatically
        // Don't await - send in background
        sendProjectUpdate({
          projectId,
          isNewProject: false, // This is an update to existing project
          // Only pass simulationOutputId if there's exactly one simulation output
          simulationOutputId:
            newSimulationIds.length === 1 ? newSimulationIds[0] : undefined,
        }).catch((error) => {
          log.error("Failed to send email notifications", error)
          // Don't show error to user - email failure shouldn't block the workflow
        })
      } catch (error) {
        log.error("Error adding to human review project", error)
        toast.error({
          title: "Failed to add to project",
          error,
          fallback: "Could not add the selected simulations. Please try again.",
        })
      } finally {
        isAddingRef.current = false
        setIsAdding(false)
      }
    },
    [
      simulationOutputIds,
      fetchProjectWithSimulationIds,
      addSimulationOutputs,
      sendProjectUpdate,
      closeMenu,
    ],
  )

  // Don't render if no simulation outputs or no projects available
  if (!simulationOutputIds.length || !humanReviewProjects?.length) {
    return null
  }

  const tooltip = title ?? "Add all simulated conversations to human review"
  const trigger = (
    <DropdownMenuTrigger asChild aria-label="Run human review">
      <Button
        variant="secondary"
        disabled={disabled}
        data-testid="run-add-to-human-review-button"
      >
        <Users />
        Human Review
      </Button>
    </DropdownMenuTrigger>
  )

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={handleOpenChange}>
      {tooltip ? (
        <Tooltip content={tooltip} triggerDisabled={disabled}>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="w-72 max-w-[calc(100vw-2rem)] p-0"
      >
        <DropdownMenuLabel className="px-3 pt-2.5 pb-0">
          Add {simulationOutputIds.length} simulation
          {simulationOutputIds.length === 1 ? "" : "s"} to:
        </DropdownMenuLabel>
        <DropdownMenuSearch
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery("")}
          onKeyDown={(event) => {
            // Enter adds to the match once the search has narrowed to one
            // project, unless it is committing an IME composition.
            if (
              event.key === "Enter" &&
              !event.nativeEvent.isComposing &&
              visibleProjects.length === 1
            ) {
              event.preventDefault()
              void handleAddToHumanReviewProject(visibleProjects[0].ulid)
            }
          }}
          placeholder="Search projects"
          aria-label="Search Human Review projects"
          data-testid="run-add-to-human-review-search"
        />
        {/* An organization can have many projects, so the list scrolls beneath
            the search field instead of running off the screen. */}
        <div className="max-h-72 overflow-y-auto p-1.5">
          {visibleProjects.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
              No matching projects
            </p>
          ) : (
            visibleProjects.map((project) => (
              <DropdownMenuItem
                key={project.ulid}
                disabled={isAdding}
                data-project-name={project.name}
                data-testid={`run-add-to-human-review-option-${project.ulid}`}
                onSelect={(event) => {
                  // The menu stays open until the update settles: success
                  // closes it, and a failure leaves it up for a retry.
                  event.preventDefault()
                  void handleAddToHumanReviewProject(project.ulid)
                }}
              >
                <TruncatedText className="min-w-0 flex-1">
                  {project.name}
                </TruncatedText>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
